// src/pages/assistant-manager/chat.jsx
import React, { useEffect, useState, useRef } from "react";
import io from "socket.io-client";
import axios from "axios";
import { Modal, Button, Form, InputGroup } from "react-bootstrap";
import Swal from "sweetalert2";
import AuthenticatedImage from "../../helpers/authenticated-image";

const Chat = () => {
  const logged_in_user_id = Number(localStorage.getItem("@user_id"));
  const [users, setUsers] = useState([]);
  const [groupUsers, setGroupUsers] = useState([]);
  const [groupSearch, setGroupSearch] = useState("");

  const [groups, setGroups] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const limit = 50;

  // Group modal
  const [showModal, setShowModal] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [showGroupMembersModal, setShowGroupMembersModal] = useState(false);
  const [currentGroupMembers, setCurrentGroupMembers] = useState([]);
  const [currentGroup, setCurrentGroup] = useState("");

  const filteredGroupUsers = groupUsers.filter((u) =>
    u.name.toLowerCase().includes(groupSearch.toLowerCase())
  );

  const [socket, setSocket] = useState(null);

  // Initialize socket
  useEffect(() => {
    if (!logged_in_user_id) return;

    const s = io(process.env.REACT_APP_WEB_SERVICES_URL, {
      withCredentials: true,
      transports: ["websocket", "polling"],
    });

    setSocket(s);

    return () => s.disconnect();
  }, [logged_in_user_id]);

  // Scroll to bottom when chat updates
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, [chatHistory]);

  // ─────────────── Refresh users + groups ───────────────
  const refreshUsersAndGroups = async () => {
    try {
      const [usersRes, groupsRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_WEB_SERVICES_URL}/assistant-manager/chat/list-users`,
          { withCredentials: true }
        ),
        axios.get(
          `${process.env.REACT_APP_WEB_SERVICES_URL}/assistant-manager/chat/list-groups`,
          { withCredentials: true }
        ),
      ]);

      setUsers(usersRes.data.users);
      setGroups(groupsRes.data.groups);
    } catch (err) {
      console.error("❌ Error refreshing users/groups:", err);
    }
  };

  // ─────────────── Load Users + Groups on mount ───────────────
  useEffect(() => {
    refreshUsersAndGroups();
    getCreateGroupUsers();
  }, []);

  // ─────────────── Load chat history + mark as read ───────────────
  useEffect(() => {
    if (!selectedChat) return;

    const loadChat = async () => {
      try {
        const url =
          selectedChat.type === "user"
            ? `${process.env.REACT_APP_WEB_SERVICES_URL}/assistant-manager/chat/history/${selectedChat.data.id}?page=${page}&limit=${limit}`
            : `${process.env.REACT_APP_WEB_SERVICES_URL}/assistant-manager/chat/history/group/${selectedChat.data.id}?page=${page}&limit=${limit}`;

        const res = await axios.get(url, { withCredentials: true });

        if (page === 0) {
          setChatHistory(res.data.messages.reverse()); // <-- reverse here
        } else {
          setChatHistory((prev) => [...res.data.messages.reverse(), ...prev]);
        }

        setTotalPages(res.data.totalPages);

        // Mark as read
        if (selectedChat.type === "user") {
          await axios.post(
            `${process.env.REACT_APP_WEB_SERVICES_URL}/assistant-manager/chat/mark-read`,
            { partner_id: selectedChat.data.id },
            { withCredentials: true }
          );
        } else {
          await axios.post(
            `${process.env.REACT_APP_WEB_SERVICES_URL}/assistant-manager/chat/mark-group-read`,
            { group_id: selectedChat.data.id },
            { withCredentials: true }
          );
        }

        refreshUsersAndGroups();
      } catch (err) {
        console.error("❌ Error loading chat or marking as read:", err);
      }
    };

    loadChat();
  }, [selectedChat, page]);

  // ─────────────── Auto-join all groups ───────────────
  useEffect(() => {
    if (!groups.length) return;
    groups.forEach((group) => {
      socket?.emit("joinGroup", group.id);
    });
  }, [groups]);

  // ─────────────── Socket listener ───────────────
  useEffect(() => {
    if (!socket) return;

    const handleMessage = (msg) => {
      if (!selectedChat) return;
      if (
        (selectedChat.type === "user" &&
          msg.receiver_id > 0 &&
          (msg.sender_id == selectedChat.data.id ||
            msg.receiver_id == selectedChat.data.id)) ||
        (selectedChat.type === "group" &&
          msg.group_id > 0 &&
          msg.group_id == selectedChat.data.id)
      ) {
        setChatHistory((prev) => [...prev, msg]);
      }

      refreshUsersAndGroups();
    };

    socket.on("chat:receive", handleMessage);
    return () => socket.off("chat:receive", handleMessage);
  }, [socket, selectedChat]);

  // ─────────────── User online/offline updates ───────────────
  useEffect(() => {
    if (!socket) return;

    const handleUserStatus = ({ user, status }) => {
      setUsers((prev) =>
        prev.map((u) =>
          u.id == user.id ? { ...u, online: status == "online" } : u
        )
      );
    };

    socket.on("user:status", handleUserStatus);
    return () => socket.off("user:status", handleUserStatus);
  }, [socket]);

  const getCreateGroupUsers = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_WEB_SERVICES_URL}/assistant-manager/chat/list-create-group-users`,
        { withCredentials: true }
      );
      setGroupUsers(res.data.users);
    } catch (err) {
      console.error("❌ Error fetching group users:", err);
    }
  };

  // ─────────────── View group members ───────────────
  const viewGroupMembers = async (group) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_WEB_SERVICES_URL}/assistant-manager/chat/group-members/${group.id}`,
        { withCredentials: true }
      );
      setCurrentGroupMembers(res.data.members);
      setCurrentGroup(group);
      setShowGroupMembersModal(true);
    } catch (err) {
      console.error("❌ Error fetching group members:", err);
      alert("Failed to fetch group members");
    }
  };

  // ─────────────── Send message ───────────────
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!selectedChat) return;

    const formData = new FormData();
    let url;
    if (selectedChat.type === "user") {
      formData.append("receiver_id", selectedChat.data.id);
      url = `${process.env.REACT_APP_WEB_SERVICES_URL}/assistant-manager/chat/send`;
    } else {
      formData.append("group_id", selectedChat.data.id);
      url = `${process.env.REACT_APP_WEB_SERVICES_URL}/assistant-manager/chat/send/group`;
    }

    if (message) formData.append("message", message);
    if (file) formData.append("file", file);

    try {
      await axios.post(url, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      setMessage("");
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      if (selectedChat.type === "user") {
        await axios.post(
          `${process.env.REACT_APP_WEB_SERVICES_URL}/assistant-manager/chat/mark-read`,
          { partner_id: selectedChat.data.id },
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_WEB_SERVICES_URL}/assistant-manager/chat/mark-group-read`,
          { group_id: selectedChat.data.id },
          { withCredentials: true }
        );
      }

      refreshUsersAndGroups();
    } catch (err) {
      console.error("❌ Error sending message:", err);
    }
  };

  // ─────────────── Create group ───────────────
  const createGroup = async () => {
    if (!groupName.trim()) return alert("Please enter a group name.");
    if (selectedMembers.length < 2)
      return alert("Please select at least 2 members.");

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_WEB_SERVICES_URL}/assistant-manager/chat/create-group`,
        { name: groupName.trim(), members: selectedMembers },
        { withCredentials: true }
      );

      if (res.data.status) {
        const newGroup = res.data.group;
        setGroups((prev) => [...prev, newGroup]);
        setSelectedChat({ type: "group", data: newGroup });
        setGroupName("");
        setSelectedMembers([]);
        setShowModal(false);
      }
    } catch (err) {
      console.error("❌ Error creating group:", err);
      alert("Failed to create group. Please try again.");
    }
  };

  // ─────────────── Remove group ───────────────
  const removeGroup = async (groupId) => {
    const result = await Swal.fire({
      title: "Delete group?",
      text: "This will permanently delete the group.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_WEB_SERVICES_URL}/assistant-manager/chat/remove-group/${groupId}`,
          { withCredentials: true }
        );

        setGroups((prev) => prev.filter((g) => g.id !== groupId));
        if (selectedChat?.data.id === groupId) setSelectedChat(null);
        Swal.fire("Deleted!", "The group has been deleted.", "success");
      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Failed to delete group.", "error");
      }
    }
  };

  // ─────────────── Remove group member ───────────────
  const removeGroupMember = async (currentGroupId, memberId) => {
    const result = await Swal.fire({
      title: "Remove member?",
      text: "This member will be removed from the group.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, remove",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_WEB_SERVICES_URL}/assistant-manager/chat/remove-group-member/${currentGroupId}/${memberId}`,
          { withCredentials: true }
        );

        setCurrentGroupMembers((prev) => prev.filter((m) => m.id !== memberId));
        Swal.fire("Removed!", "The member has been removed.", "success");
      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Failed to remove member.", "error");
      }
    }
  };

  // ─────────────── Filtered users ───────────────
  const filteredUsers = users.filter((u) =>
    u.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ─────────────── Delete private chat ───────────────
  const deletePrivateChat = async (partnerId) => {
    const result = await Swal.fire({
      title: "Delete chat?",
      text: "This will permanently delete your private conversation.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_WEB_SERVICES_URL}/assistant-manager/chat/delete`,
          {
            data: { user_id: partnerId },
            withCredentials: true,
          }
        );

        if (selectedChat?.type === "user" && selectedChat.data.id === partnerId) {
          setSelectedChat(null);
          setChatHistory([]);
        }

        refreshUsersAndGroups();

        Swal.fire("Deleted!", "The chat has been deleted.", "success");
      } catch (err) {
        console.error("❌ Error deleting chat:", err);
        Swal.fire("Error", "Failed to delete chat.", "error");
      }
    }
  };

  return (
    <div className="container-fluid mt-3">
      <div className="row">
        {/* Sidebar */}
        <div className="col-md-3 d-flex flex-column border-end">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="m-0">Chats</h5>
            <Button size="sm" variant="success" onClick={() => setShowModal(true)}>
              + Group
            </Button>
          </div>

          {/* Search box */}
          <Form.Control
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mb-3"
          />

          <h6>Users</h6>
          <ul
            className="list-group mb-3 overflow-auto"
            style={{ maxHeight: "500px" }}
          >
            {filteredUsers.map((u) => (
              <li
                key={u.id}
                className={`list-group-item d-flex justify-content-between align-items-center ${selectedChat?.type === "user" &&
                    selectedChat?.data.id === u.id
                    ? "active"
                    : ""
                  }`}
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setSelectedChat({ type: "user", data: u });
                  setPage(0); // reset page on new chat
                }}
              >
                <span>
                  {u.name} ({u.role})
                </span>

                <div className="d-flex align-items-center gap-2">
                  {u.unread_count > 0 && (
                    <span className="badge bg-danger">{u.unread_count}</span>
                  )}
                  {u.online && (
                    <span style={{ color: "green", fontSize: "1.2rem" }}>●</span>
                  )}

                 
                </div>
              </li>
            ))}
          </ul>

          <h6>Groups</h6>
          <ul className="list-group overflow-auto">
            {groups.map((g) => (
              <li
                key={g.id}
                className={`list-group-item d-flex justify-content-between align-items-center ${selectedChat?.type === "group" && selectedChat?.data.id == g.id
                    ? "active"
                    : ""
                  }`}
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setSelectedChat({ type: "group", data: g });
                  setPage(0); // reset page on new chat
                }}
              >
                <span>{g.name}</span>

                <div className="d-flex align-items-center gap-2">
                  {g.unread_count > 0 && (
                    <span className="badge bg-danger">{g.unread_count}</span>
                  )}

                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      viewGroupMembers(g);
                    }}
                    style={{ color: "blue", cursor: "pointer" }}
                    title="Group Members"
                  >
                    👤
                  </span>

                    {g.created_by == logged_in_user_id && (
                    <span
                      style={{ cursor: "pointer", color: "red" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeGroup(g.id);
                      }}
                      title="Delete Group"
                    >
                      🗑
                    </span>
                    )}


                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Chat Area */}
        <div className="col-md-9 d-flex flex-column" style={{ height: "85vh" }}>
          {selectedChat ? (
            <>
              {/* Chat header */}
              <div className="border-bottom py-2 px-3 bg-light d-flex justify-content-between align-items-center">
                <h6 className="m-0">
                  {selectedChat.type === "user"
                    ? selectedChat.data.name
                    : `${selectedChat.data.name} (Group)`}
                </h6>
              </div>

              {/* Load older messages */}
              {page + 1 < totalPages && (
                <div className="text-center my-2">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => setPage((prev) => prev + 1)}
                  >
                    Load older messages
                  </button>
                </div>
              )}

              {/* Messages */}
              <div
                ref={messagesContainerRef}
                className="flex-grow-1 overflow-auto p-3 bg-white"
                style={{ maxHeight: "70vh" }}
              >
                {chatHistory.map((c) => (
                  <div
                    key={c.id}
                    className={`d-flex mb-2 ${c.sender_id == logged_in_user_id
                        ? "justify-content-end"
                        : "justify-content-start"
                      }`}
                  >
                    <div
                      className={`p-2 rounded shadow-sm text-white ${c.sender_id == logged_in_user_id
                          ? "bg-secondary"
                          : "bg-success"
                        }`}
                      style={{ maxWidth: "70%" }}
                    >



                      {selectedChat.type === "group" && (
                        <div className="fw-bold small">{c.sender_name}</div>
                      )}

                      {c.message && <div>{c.message}</div>}

                      {c.file_url && (
                        <>
                          {c.file_type === "image" ? (
                          
      // 👈 Use the imported component here, passing c.file_url
      <AuthenticatedImage fileUrl={`${process.env.REACT_APP_WEB_SERVICES_URL}/assistant-manager${c.file_url}`} /> 
    
                          ) : (
                            <div
                              className="p-2 mt-2 bg-light text-dark rounded d-flex align-items-center"
                              style={{
                                width: "250px",
                                border: "1px solid #ddd",
                              }}
                            >
                              {/* PDF icon */}
                              {c.file_type === "pdf" && (
                                <span
                                  style={{
                                    background: "url('/pdf-icon.png') center/contain no-repeat",
                                    width: "50px",
                                    height: "50px",
                                    marginRight: "10px",
                                  }}
                                ></span>
                              )}

                              {/* DOC icon */}
                              {c.file_type === "doc" && (
                                <span
                                  style={{
                                    background: "url('/doc-icon.png') center/contain no-repeat",
                                    width: "50px",
                                    height: "50px",
                                    marginRight: "10px",
                                  }}
                                ></span>
                              )}

                              {/* Generic file icon */}
                              {c.file_type === "other" && (
                                <span
                                  style={{
                                    background: "url('/file-icon.png') center/contain no-repeat",
                                    width: "50px",
                                    height: "50px",
                                    marginRight: "10px",
                                  }}
                                ></span>
                              )}

                              <div className="flex-grow-1">
                                <div className="small fw-bold">{c.file_name}</div>
                                <a
                                  href={`${process.env.REACT_APP_WEB_SERVICES_URL}/assistant-manager${c.file_url}`}
                                  className="text-primary small"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Download
                                </a>
                              </div>
                            </div>
                          )}
                        </>
                      )}





                    </div>
                  </div>
                ))}
              </div>

              {/* Input */}
              <form onSubmit={sendMessage} className="d-flex gap-2 border-top p-2 bg-light">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Type a message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
                <input type="file" ref={fileInputRef} onChange={(e) => setFile(e.target.files[0])} />
                <button className="btn btn-success" type="submit">
                  Send
                </button>
              </form>
            </>
          ) : (
            <div className="d-flex justify-content-center align-items-center flex-grow-1">
              <h5 className="text-muted">Select a chat to start messaging</h5>
            </div>
          )}
        </div>
      </div>

      {/* Create Group Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-2">
              <Form.Label>Group Name</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Enter group name"
                />
                <Button variant="primary" onClick={createGroup}>
                  Create
                </Button>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Search Members</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search..."
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Select Members</Form.Label>
              {filteredGroupUsers.map((u) => (
                <div
                  key={u.id}
                  className="d-flex align-items-center mb-1"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    if (selectedMembers.includes(u.id)) {
                      setSelectedMembers((prev) => prev.filter((id) => id !== u.id));
                    } else {
                      setSelectedMembers((prev) => [...prev, u.id]);
                    }
                  }}
                >
                  <Form.Check type="checkbox" checked={selectedMembers.includes(u.id)} readOnly className="me-2" />
                  <span>{u.name}</span>
                </div>
              ))}
            </Form.Group>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Group Members Modal */}
      <Modal show={showGroupMembersModal} onHide={() => setShowGroupMembersModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{currentGroup?.name} Members</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ul className="list-group">
            {currentGroupMembers.map((member) => (
              <li key={member.id} className="list-group-item d-flex justify-content-between align-items-center">
                {member.name} ({member.role})
                {logged_in_user_id === currentGroup?.created_by && (
                  <span
                    style={{ cursor: "pointer", color: "red" }}
                    onClick={() => removeGroupMember(currentGroup.id, member.id)}
                    title="Remove Member"
                  >
                    ❌
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Chat;
